import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { supabase } from './supabaseClient';
import { GoogleMap, useJsApiLoader, InfoWindow } from '@react-google-maps/api';
import { FaFileLines, FaHouseFloodWater, FaMapPin, FaTruck, FaExclamation, FaFire, FaHeart  } from 'react-icons/fa6';
import Preloader from './Preloader'; 
import ReactDOMServer from 'react-dom/server';
import { MarkerClusterer } from '@googlemaps/markerclusterer';

const libraries = ['places', 'marker'];

const renderer = {
  render: ({ count, position, markers }) => {
    try {
      const clusterType = markers[0]?.incident?.type?.toLowerCase() || 'others';
      
      const getClusterColor = (type) => {
        switch (type) {
          case 'fire': return '#e65100';
          case 'flood': return '#1976d2';
          case 'medical': return '#d32f2f';
          case 'rescue': return '#388e3c';
          default: return '#fbc02d';
        }
      };

      const clusterColor = getClusterColor(clusterType);

      // New SVG with a larger outer circle for scope and the original inner circle for count.
      const svg = `
        <svg width="120" height="120" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">
          {/* Outer radius circle */}
          <circle
            cx="60"
            cy="60"
            r="58"
            fill="${clusterColor}"
            fill-opacity="0.2"
            stroke="${clusterColor}"
            stroke-width="1"
          />

          {/* Inner count circle (the "glow" one) */}
          <defs>
            <radialGradient id="grad1" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
              <stop offset="40%" stop-color="${clusterColor}" stop-opacity="0.9"/>
              <stop offset="100%" stop-color="${clusterColor}" stop-opacity="0.1"/>
            </radialGradient>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="rgba(0,0,0,0.3)"/>
            </filter>
          </defs>
          <circle cx="60" cy="60" r="24" fill="url(#grad1)" filter="url(#shadow)"/>

          {/* Count text */}
          <text x="60" y="66" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="18" font-weight="bold" style="text-shadow: 0 0 3px rgba(0,0,0,0.5);">${count}</text>
        </svg>
      `;

      return new window.google.maps.Marker({
        position,
        icon: {
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
          scaledSize: new window.google.maps.Size(120, 120),
          anchor: new window.google.maps.Point(60, 60),
        },
        zIndex: 10,
      });
    } catch (e) {
      console.error('Error in custom cluster renderer:', e);
      return new window.google.maps.Marker({ position });
    }
  },
};

function IncidentMap() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapCenter, setMapCenter] = useState({ lat: -6.2088, lng: 106.8456 }); 
  const [totalReports, setTotalReports] = useState(0);
  const [fireReports, setFireReports] = useState(0);
  const [floodReports, setFloodReports] = useState(0);
  const [medicalReports, setMedicalReports] = useState(0);
  const [rescueReports, setRescueReports] = useState(0);
  const [otherReports, setOtherReports] = useState(0);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [map, setMap] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA",
    libraries
  });

  const mapContainerStyle = {
    width: '100%',
    height: '600px',
    borderRadius: '12px',
    maxWidth: '100%'
  };

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    mapId: 'YOUR_MAP_ID',
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }]
      }
    ]
  }), []);

  useEffect(() => {
      document.title = "Incident Map";
    }, []);

  useEffect(() => {
    fetchIncidents();

    const incidentsChannel = supabase
      .channel('public:incident_reports_map')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'incident_reports' },
        (payload) => {
          console.log('Incident report change received on map!', payload);
          fetchIncidents();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(incidentsChannel);
    };
  }, []);

  useEffect(() => {
    if (!map || !isLoaded || incidents.length === 0) return;

    // Filter incidents based on the selected type
    const incidentsToDisplay = typeFilter === 'all'
      ? incidents
      : incidents.filter(i => i.type.toLowerCase() === typeFilter);

    // Group the filtered incidents by type
    const incidentsByType = {};
    incidentsToDisplay.forEach(incident => {
      const type = incident.type.toLowerCase();
      if (!incidentsByType[type]) {
        incidentsByType[type] = [];
      }
      incidentsByType[type].push(incident);
    });

    // Create separate clusterers for each type
    const clusterers = [];
    
    Object.entries(incidentsByType).forEach(([type, typeIncidents]) => {
      const markers = typeIncidents.map((incident) => {
        const marker = new window.google.maps.Marker({
          position: {
            lat: incident.location_coords.latitude,
            lng: incident.location_coords.longitude,
          },
          icon: {
            url: getMarkerIcon(incident.type),
            scaledSize: new window.google.maps.Size(32, 32),
            anchor: new window.google.maps.Point(16, 16),
          },
          title: incident.location,
        });

        // Attach incident data to the marker for the renderer to use
        marker.incident = incident;

        marker.addListener('click', () => {
          setSelectedIncident(incident);
        });
        
        return marker;
      });

      // Create clusterer for this type with custom renderer
      const markerClusterer = new MarkerClusterer({ 
        map, 
        markers, 
        renderer
      });
      
      clusterers.push(markerClusterer);
    });

    return () => {
      clusterers.forEach(clusterer => clusterer.clearMarkers());
    };
  }, [map, isLoaded, incidents, typeFilter]);

  async function fetchIncidents() {
    setLoading(true);
    setError('');
    const { data, error } = await supabase
      .from('incident_reports')
      .select('*, location_coords');
    
    if (error) {
      console.error('Error fetching incidents:', error);
      setError('Failed to load incidents');
      setIncidents([]);
      setTotalReports(0);
      setFireReports(0);
      setFloodReports(0);
      setMedicalReports(0);
      setRescueReports(0);
      setOtherReports(0);
    } else {
      const incidentsWithCoords = data.filter(incident => 
        incident.location_coords && 
        typeof incident.location_coords.latitude === 'number' &&
        typeof incident.location_coords.longitude === 'number' &&
        (incident.status !== 'archived')
      );
      setIncidents(incidentsWithCoords);

      // Calculate counts
      let fireCount = 0;
      let floodCount = 0;
      let medicalCount = 0;
      let rescueCount = 0;
      let otherCount = 0;

      incidentsWithCoords.forEach(incident => {
        switch (incident.type.toLowerCase()) {
          case 'fire':
            fireCount++;
            break;
          case 'flood':
            floodCount++;
            break;
          case 'medical':
            medicalCount++;
            break;
          case 'rescue':
            rescueCount++;
            break;
          case 'others':
            otherCount++;
            break;
          default:
            otherCount++;
            break;
        }
      });

      setTotalReports(incidentsWithCoords.length);
      setFireReports(fireCount);
      setFloodReports(floodCount);
      setMedicalReports(medicalCount);
      setRescueReports(rescueCount);
      setOtherReports(otherCount);

      if (incidentsWithCoords.length > 0) {
        setMapCenter({ 
          lat: incidentsWithCoords[0].location_coords.latitude,
          lng: incidentsWithCoords[0].location_coords.longitude
        });
      }
    }
    setLoading(false);
  }

  const getMarkerIcon = (type) => {
    let iconComponent;
    const size = 32; 

    switch (type.toLowerCase()) {
        case 'fire':
            iconComponent = <FaFire color='#e65100' size={size} />;
            break;
        case 'flood':
            iconComponent = <FaHouseFloodWater color='#1976d2' size={size} />;
            break;
        case 'medical':
            iconComponent = <FaHeart color='#d32f2f' size={size} />;
            break;
        case 'rescue':
            iconComponent = <FaTruck color='#388e3c' size={size} />;
            break;
        case 'others':
            iconComponent = <FaExclamation color='#fbc02d' size={size} />;
            break;
        default:
            iconComponent = <FaMapPin color='#666' size={size} />;
            break;
    }
    
    const svgString = ReactDOMServer.renderToString(iconComponent);
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgString)}`;
  };

  if (loading) return <Preloader text="Loading Map..." />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, minWidth: 0 }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
                isOpen={showMobileMenu}
                onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className='title-text'>Incident Map</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />

        <div style={{
          display: 'flex', 
          flexWrap: 'nowrap',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          gap: 16,
          marginBottom: 24,
          width: '100%',
          overflowX: 'auto',
        }}>
          <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 260, background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#666', marginBottom: 8 }}>Total Reports Today</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold' }}>{totalReports}</div>
              <span style={{ fontSize: 28, color: '#666' }}><FaFileLines /></span>
            </div>
          </div>
          <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 260, background: '#ffe0b2', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#e65100', marginBottom: 8 }}>Fire Reports</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#e65100' }}>{fireReports}</div>
              <span style={{ fontSize: 28, color: '#e65100' }}><FaFire /></span>
            </div>
          </div>
          <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 260, background: '#bbdefb', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#1976d2', marginBottom: 8 }}>Flooding Reports</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#1976d2' }}>{floodReports}</div>
              <span style={{ fontSize: 28, color: '#1976d2' }}><FaHouseFloodWater /></span>
            </div>
          </div>
          <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 260, background: '#ffcdd2', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#d32f2f', marginBottom: 8 }}>Medical Reports</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#d32f2f' }}>{medicalReports}</div>
              <span style={{ fontSize: 28, color: '#d32f2f' }}><FaHeart /></span>
            </div>
          </div>
          <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 260, background: '#c8e6c9', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#388e3c', marginBottom: 8 }}>Rescue Reports</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#388e3c' }}>{rescueReports}</div>
              <span style={{ fontSize: 28, color: '#388e3c'}}><FaTruck /></span>
            </div>
          </div>
          <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 260, background: '#fff9c4', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, color: '#fbc02d', marginBottom: 8 }}>Others</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 'bold', color: '#fbc02d' }}>{otherReports}</div>
              <span style={{ fontSize: 28, color: '#fbc02d' }}><FaExclamation /></span>
            </div>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', maxWidth: '100%' }}>
          <div style={{ position: 'relative', maxWidth: '100%' }}>
            {/* Filter Dropdown */}
            <div style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255,255,255,0.9)',
              padding: 12,
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 101
            }}>
              <select 
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', fontWeight: 'bold' }}
              >
                <option value="all">All Types</option>
                <option value="fire">Fire</option>
                <option value="flood">Flood</option>
                <option value="medical">Medical</option>
                <option value="rescue">Rescue</option>
                <option value="others">Others</option>
              </select>
            </div>
            {/* Legend */}
            <div style={{
              position: 'absolute',
              top: 20,
              left: 20,
              background: 'rgba(255,255,255,0.9)',
              padding: 12,
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              zIndex: 100
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Legend:</div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}><FaFire style={{ color: '#e65100',width: 24, height: 24, marginRight: 8 }} />Fire</div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}><FaHouseFloodWater style={{color: '#1976d2', width: 24, height: 24, marginRight: 8 }} />Flood</div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}><FaHeart style={{color: '#d32f2f', width: 24, height: 24, marginRight: 8 }}/>Medical</div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}><FaTruck style={{ color: '#388e3c', width: 24, height: 24, marginRight: 8 }} />Rescue</div>
              <div style={{ display: 'flex', alignItems: 'center' }}><FaExclamation style={{ color: '#fbc02d', width: 24, height: 24, marginRight: 8 }} />Others</div>
            </div>
            {/* Map */}
            {isLoaded ? (
              <div style={{ width: '100%', maxWidth: '100%' }}>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={12}
                  options={mapOptions}
                  onLoad={setMap}
                >
                {selectedIncident && (
                  <InfoWindow
                    position={{
                      lat: selectedIncident.location_coords.latitude,
                      lng: selectedIncident.location_coords.longitude,
                    }}
                    onCloseClick={() => setSelectedIncident(null)}
                  >
                    <div style={{ minWidth: 220, maxWidth: 300 }}>
                      <div style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 4 }}>{selectedIncident.type.charAt(0).toUpperCase() + selectedIncident.type.slice(1)} Report</div>
                      <div style={{ fontSize: 14, marginBottom: 4 }}><b>Status:</b> {selectedIncident.status ? selectedIncident.status.charAt(0).toUpperCase() + selectedIncident.status.slice(1) : 'N/A'}</div>
                      <div style={{ fontSize: 14, marginBottom: 4 }}><b>Severity:</b> {selectedIncident.severity ? selectedIncident.severity.charAt(0).toUpperCase() + selectedIncident.severity.slice(1) : 'N/A'}</div>
                      <div style={{ fontSize: 14, marginBottom: 4 }}><b>Location:</b> {selectedIncident.location}</div>
                      <div style={{ fontSize: 14, marginBottom: 4 }}><b>Time:</b> {selectedIncident.created_at ? new Date(selectedIncident.created_at).toLocaleString() : 'N/A'}</div>
                      {selectedIncident.image_url && (
                        <img src={selectedIncident.image_url} alt="Incident" style={{ width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 6, marginTop: 8 }} />
                      )}
                      {!selectedIncident.image_url && (
                        <div style={{ width: '100%', height: 80, background: '#eee', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', marginTop: 8 }}>No Image</div>
                      )}
                    </div>
                  </InfoWindow>
                )}
                </GoogleMap>
              </div>
            ) : (
              <div style={{
                width: '100%',
                height: '600px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                Loading Map...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default IncidentMap;